import { Box, Container, Typography } from '@mui/material';
import type { FC } from 'react';
import { PortableText } from '~/components/atoms/PortableText';
import type { VideoTextProps } from '~/types/sanity/components';
import { getCompoundTestIdGenerator } from '~/utils/getCompoundTestIdGenerator';
import { isPortableTextBlockArray } from '~/utils/portableTextUtils';
import { VideoPlayer } from './VideoPlayer';
import type { VideoSources } from './VideoText.types';

/**
 * Displays a video with accompanying text. Video can be placed in the left or
 * right column on desktop. On mobile, the video is displayed above the text.
 */
export const VideoText: FC<Omit<VideoTextProps, '_key' | '_type'>> = ({
  backgroundColor,
  id,
  placement,
  poster,
  text,
  title,
  video,
}) => {
  const sources: VideoSources = video.url && video.mimeType ? [{ src: video.url, type: video.mimeType }] : [];
  const getCompoundTestId = getCompoundTestIdGenerator('videoText');

  return sources.length ? (
    <Box
      component="section"
      py={{ xs: 5, sm: 7.5, md: 10.625 }}
      bgcolor={({ palette }) => (backgroundColor === 'grey' ? palette.grey[100] : 'transparent')}
    >
      <Container>
        <Box
          display={{ md: 'grid' }}
          gridTemplateColumns={{
            md: placement === 'right' ? '1fr 535px' : '535px 1fr',
            lg: placement === 'right' ? '1fr 648px' : '648px 1fr',
          }}
          gridTemplateAreas={{
            md: placement === 'right' ? `'header video' 'content video'` : `'video header' 'video content'`,
          }}
          columnGap={{ md: 6.25, lg: 16 }}
          textAlign={{ md: placement === 'right' ? 'right' : 'left' }}
        >
          {/* Title */}
          <Box mb={{ xs: 3 }} gridArea={{ md: 'header' }} order={{ xs: 1, md: 0 }}>
            <Typography
              variant="h5"
              component="h2"
              fontSize={({ typography }) => ({ sm: typography.h3.fontSize })}
              lineHeight={({ typography }) => ({ sm: typography.h3.lineHeight })}
            >
              {title}
            </Typography>
          </Box>

          {/* Video */}
          <Box mb={{ xs: 3.75, md: 0 }} gridArea={{ md: 'video' }} order={{ xs: 2, md: 0 }}>
            <VideoPlayer id={id} poster={poster} sources={sources} />
          </Box>

          {/* Text */}
          <Box gridArea={{ md: 'content' }} order={{ xs: 3, md: 0 }}>
            {isPortableTextBlockArray(text) && <PortableText portableText={text} testId={getCompoundTestId()} />}
          </Box>
        </Box>
      </Container>
    </Box>
  ) : null;
};
